import { getFreshTokensAndClaims, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
  UIAppliedAIService,
  UIIDService, UIJobService,
  UIWorkAndSafetyService,
} from "@initia-digital/data-sdk";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";

import { IAzureAdUser, IInitiaIdentity, WithProfileImage } from "@initia-digital/types/identity";
import { ISalesforceJob, SalesforceJobMin } from "@initia-digital/types/job";
import { CredentialInput } from "@initia-digital/types/service";
import {
  IContractorMin, IContractorType, ICriticalOrSiteSpecificHazard,
  IEquipmentClass, IHazard, IHazardClass, IPlan,
  IPrestart, ITag, IPrestartHazard,
  IHazardMedia
} from "@initia-digital/types/work-and-safety";
import { wrapRefreshCognitoCredentialsUtil } from "./auth";


const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' })


// --------------------------------------------
//              IDENTITY SERVICE
// --------------------------------------------
const idService = new UIIDService({
  env: import.meta.env.VITE_ENV_LOWER,
  credentialProviderProps: {
    authProvider: import.meta.env.VITE_AUTH_PROVIDER,
    region: 'ap-southeast-2',
    identityPoolId: import.meta.env.VITE_AUTH_COGNITO_IDENTITY_POOL_ID,
  },
  logger
});

// Fetches the identity of the authnenticated user 
export const fetchMsalIdentity = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, idTokenClaims }  = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (
    !('preferred_username' in idTokenClaims)
    || !baseDefinedAndNotNullNarrowing(idTokenClaims?.preferred_username)
    || typeof idTokenClaims?.preferred_username !== 'string'
  ) {
    return null;
  }

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = idService.getApiClient();
  const response = await apiClient.request<IInitiaIdentity>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/identity/:email',
      pathParameters: {
        email: idTokenClaims.preferred_username
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_ID_API_KEY,
        'id-token': idToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

// Fetches the identity of the authnenticated user 
export const fetchIdentityByEmail = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, email: string) => {
  // --------------------------------------------
  if (
    !baseDefinedAndNotNullNarrowing(email)
  ) {
    return null;
  }
  const { msal } = authDetail;
  const { idToken }  = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = idService.getApiClient();
  const response = await apiClient.request<IInitiaIdentity>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/identity/:email',
      pathParameters: {
        email
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_ID_API_KEY,
        'id-token': idToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchMsGroup = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, groupId: string) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = idService.getApiClient();
  const response = await apiClient.request<WithProfileImage<IAzureAdUser>[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/group/:id',
      pathParameters: {
        id: groupId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_ID_API_KEY,
        'id-token': idToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

// --------------------------------------------
//              JOB SERVICE
// --------------------------------------------
const jobService = new UIJobService({
  env: import.meta.env.VITE_ENV_LOWER,
  credentialProviderProps: {
    authProvider: import.meta.env.VITE_AUTH_PROVIDER,
    region: 'ap-southeast-2',
    identityPoolId: import.meta.env.VITE_AUTH_COGNITO_IDENTITY_POOL_ID,
  },
  logger
});



// Fetches the list of jobs for the authenticated user that are at the specified stages (default: Proposal,In Progress,In Transition)
export const searchJobList = async (
  q: string,
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  signal?: AbortSignal
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  
  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = jobService.getApiClient();
  const response = await apiClient.request<SalesforceJobMin[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/sf-job',
      queryStringParameters: {
        q,
        limit: '20',
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_JOB_API_KEY,
        'id-token': idToken
      },
      signal
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchUserJobList = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, jobStages?: string | null) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, idTokenClaims } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (
    !('name' in idTokenClaims)
    || !baseDefinedAndNotNullNarrowing(idTokenClaims?.name)
    || typeof idTokenClaims?.name !== 'string'
  ) {
    return [];
  }

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = jobService.getApiClient();
  const response = await apiClient.request<ISalesforceJob[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/sf-job',
      queryStringParameters: {
        pm: idTokenClaims.name,
        stages: baseDefinedAndNotNullNarrowing(jobStages) ? jobStages : 'Proposal,In Progress,In Transition'
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_JOB_API_KEY,
        'id-token': idToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


// Fetches the detailed data for the specified job
export const fetchJobDetail = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, jobId: string) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (!baseDefinedAndNotNullNarrowing(jobId)) throw new Error('jobId is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const apiClient = jobService.getApiClient();
  const response = await apiClient.request<ISalesforceJob>(
    {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput,
    {
      method: 'GET',
      templatePath: '/sf-job/:jobId',
      pathParameters: {
        jobId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_JOB_API_KEY,
        'id-token': idToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

// --------------------------------------------
//           WORK AND SAFETY SERVICE
// --------------------------------------------
const wsService = new UIWorkAndSafetyService({
  env: import.meta.env.VITE_ENV_LOWER,
  credentialProviderProps: {
    authProvider: import.meta.env.VITE_AUTH_PROVIDER,
    region: 'ap-southeast-2',
    identityPoolId: import.meta.env.VITE_AUTH_COGNITO_IDENTITY_POOL_ID,
  },
  logger
});

export const fetchPlanList = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  query: {
    jobId?: string,
    planMonth?: string,
    createdBy?: string
  }
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IPlan[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/plan',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchPlanDetail = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>, planId: string
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (!baseDefinedAndNotNullNarrowing(planId)) throw new Error('planId is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IPlan>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/plan/:id',
      pathParameters: {
        id: planId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchPlanPdf = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, jobId: string,planId: string) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  
  if (!baseDefinedAndNotNullNarrowing(jobId)) throw new Error('jobId is not defined');
  if (!baseDefinedAndNotNullNarrowing(planId)) throw new Error('planId is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<{ path: string }>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/job/:jobId/plan-pdf/:planId',
      pathParameters: {
        jobId,
        planId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchPrestartList = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  query: {
    jobId?: string,
    prestartDate?: string,
    createdBy?: string,
    planId?: string
  }
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);
  
  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IPrestart[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/prestart',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchPrestartDetail = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>, prestartId: string
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (!baseDefinedAndNotNullNarrowing(prestartId)) throw new Error('prestartId is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IPrestart>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/prestart/:id',
      pathParameters: {
        id: prestartId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchPrestartPdf = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  jobId: string,
  planId: string,
  prestartId: string
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  
  if (!baseDefinedAndNotNullNarrowing(jobId)) throw new Error('jobId is not defined');
  if (!baseDefinedAndNotNullNarrowing(planId)) throw new Error('planId is not defined');
  if (!baseDefinedAndNotNullNarrowing(prestartId)) throw new Error('prestartId is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<{ path: string }>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/job/:jobId/plan/:planId/prestart-pdf/:prestartId',
      pathParameters: {
        jobId,
        planId,
        prestartId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchTagList = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  query: {
    type: string
  }
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
  if (!baseDefinedAndNotNullNarrowing(query.type)) throw new Error('type is not defined');

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<ITag[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/tag',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchContractorList = async (
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IContractorMin[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/contractor',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchContractorTypeList = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IContractorType[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/contractor-type',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchHazardClassList = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, query?: { status?: string }) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IHazardClass[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/hazard-class',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

export const fetchHazardList = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, query?: { hazardClass?: string, status?: string }) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IHazard[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/hazard',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}

type MonthSummaryResponse = {
  planMonthCounts: {
    month: string,
    count: string
  }[],
  prestartMonthCounts: {
    month: string,
    count: string
  }[],
  currentHighlightedHazards: ICriticalOrSiteSpecificHazard[]
}

type JobHazardSummaryResponse = {
  criticalHazards: ICriticalOrSiteSpecificHazard[],
  hazardObservations: IPrestartHazard[]
  hazardObsMedia: IHazardMedia[]
}

type OlapQueryResponse<T extends { monthSummary?: string, jobHazardSummary?: string }> = 
  T extends { monthSummary: 'true' } ? MonthSummaryResponse :
  T extends { jobHazardSummary: 'true', jobId: string } ? JobHazardSummaryResponse :
  never;

export const fetchOlapQuery = async <T extends {
  monthSummary?: string,
  jobHazardSummary?: string,
  jobId?: string
}>(
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
  query: T
) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<OlapQueryResponse<T>>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/olap-query',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )
  return response.data;
}


export const fetchEquipmentClassList = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, query?: { type?: string}) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request<IEquipmentClass[]>(
    credentialsInput,
    {
      method: 'GET',
      templatePath: '/equipment-class',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      } 
    },
    wrapRefreshCredentials
  )
  return response.data;
}


// --------------------------------------------
//           APPLIED AI SERVICE
// --------------------------------------------
const appAiService = new UIAppliedAIService({
  env: import.meta.env.VITE_ENV_LOWER,
  credentialProviderProps: {
    authProvider: import.meta.env.VITE_AUTH_PROVIDER,
    region: 'ap-southeast-2',
    identityPoolId: import.meta.env.VITE_AUTH_COGNITO_IDENTITY_POOL_ID,
  },
  logger
});

export const generateAITags = async (authDetail: Awaited<ReturnType<typeof useMSALPayload>>, query: {
  maxTags?: number, outputSpec?: string, temperature?: number
}, body: { content: string }) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = appAiService.getApiClient();
  const response = await apiClient.request<{
    tags: string[]
  }>(
    credentialsInput,
    {
      method: 'POST',
      templatePath: '/generate/tags',
      queryStringParameters: query,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_APP_AI_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      },
      body
    },
    wrapRefreshCredentials
  )
  return response.data;
}